import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Loader2, Lock, Tag, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
export const PaymentGate = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [loading, setLoading] = useState(true);
  const [hasPaid, setHasPaid] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [redeemingCode, setRedeemingCode] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    checkPaymentStatus();
    trackAbandonment();
  }, []);
  const trackAbandonment = async () => {
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) return;

      // Track that user viewed payment gate (potential abandoned checkout)
      await supabase.from("abandoned_checkouts").insert({
        user_id: session.user.id,
        email: session.user.email || "",
        amount: 10000 // $100 in cents
      });
    } catch (error) {
      console.error("Error tracking abandonment:", error);
      // Don't block the user experience if tracking fails
    }
  };
  const checkPaymentStatus = async () => {
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }
      const {
        data: payment,
        error
      } = await supabase.from("user_payments").select("*").eq("user_id", session.user.id).eq("status", "paid").maybeSingle();
      
      // If payment is verified, redirect to dashboard
      if (payment && window.location.pathname === "/payment-gate") {
        navigate("/dashboard");
        return;
      }
      if (error) {
        console.error("Error checking payment:", error);
      }
      setHasPaid(!!payment);
    } catch (error) {
      console.error("Payment check error:", error);
    } finally {
      setLoading(false);
    }
  };
  const handlePayment = () => {
    window.open("https://buy.stripe.com/dRm7sM6Xdbmhauu4SpdQQ00", "_blank");
    toast.success("Opening payment page...");
  };
  const handleRedeemCode = async () => {
    if (!discountCode.trim()) {
      toast.error("Please enter a discount code");
      return;
    }
    setRedeemingCode(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke("redeem-discount-code", {
        body: {
          code: discountCode.trim()
        }
      });
      if (error) throw error;
      toast.success(data.message || "Code redeemed successfully!");
      await checkPaymentStatus();
    } catch (error: any) {
      console.error("Discount code error:", error);
      toast.error(error.message || "Invalid discount code");
    } finally {
      setRedeemingCode(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>;
  }
  if (!hasPaid) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background/95 to-muted/20 px-4">
        <div className="max-w-2xl w-full space-y-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        <Card className="w-full">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-3xl">Unlock Full Access</CardTitle>
            <CardDescription className="text-lg">
              Get lifetime access to Launch To Revenue course and Skool community
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <p className="text-muted-foreground">Complete digital marketing framework</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <p className="text-muted-foreground">Access to exclusive Skool community</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <p className="text-muted-foreground">Lifetime course updates and resources</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <p className="text-muted-foreground">$200</p>
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="flex items-baseline justify-center gap-2 mb-6">
                <span className="text-5xl font-bold">$200</span>
                <span className="text-muted-foreground">one-time payment</span>
              </div>

              <Button size="lg" className="w-full" onClick={handlePayment}>
                Get Access Now
              </Button>

              <p className="text-xs text-center text-muted-foreground mt-4">
                Secure payment processed by Stripe
              </p>
            </div>

            <div className="border-t pt-6">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Have a discount code?</span>
              </div>
              <div className="flex gap-2">
                <Input placeholder="Enter code" value={discountCode} onChange={e => setDiscountCode(e.target.value.toUpperCase())} disabled={redeemingCode} className="flex-1" />
                <Button variant="outline" onClick={handleRedeemCode} disabled={redeemingCode || !discountCode.trim()}>
                  {redeemingCode ? <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Applying...
                    </> : "Apply"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>;
  }
  return <>{children}</>;
};